import { useEffect, useRef, useState } from "react";
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle,
  AlertDialogDescription, AlertDialogFooter, AlertDialogCancel, AlertDialogAction,
} from "@/components/ui/alert-dialog";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";

let _emit = null;

// Promise-based on-brand confirm. Falls back to window.confirm if the host isn't mounted.
export function confirmDialog(opts = {}) {
  return new Promise((resolve) => {
    if (!_emit) { resolve(window.confirm(opts.description || opts.title || "Are you sure?")); return; }
    _emit({ kind: "confirm", opts, resolve });
  });
}

// Promise-based on-brand prompt. Resolves to the string, or null on cancel.
export function promptDialog(opts = {}) {
  return new Promise((resolve) => {
    if (!_emit) { resolve(window.prompt(opts.description || opts.title || "", opts.defaultValue || "")); return; }
    _emit({ kind: "prompt", opts, resolve });
  });
}

export function DialogHost() {
  const [state, setState] = useState(null);
  const [val, setVal] = useState("");
  const resolver = useRef(null);

  useEffect(() => {
    _emit = ({ kind, opts, resolve }) => {
      resolver.current = resolve;
      setVal(opts.defaultValue || "");
      setState({ kind, opts });
    };
    return () => { _emit = null; };
  }, []);

  const close = (result) => {
    const r = resolver.current;
    resolver.current = null;
    setState(null);
    if (r) r(result);
  };

  if (!state) return null;
  const o = state.opts;

  if (state.kind === "confirm") {
    const destructive = o.tone === "destructive";
    return (
      <AlertDialog open onOpenChange={(open) => { if (!open) close(false); }}>
        <AlertDialogContent data-testid="confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>{o.title || "Are you sure?"}</AlertDialogTitle>
            {o.description && <AlertDialogDescription>{o.description}</AlertDialogDescription>}
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="confirm-cancel" onClick={() => close(false)}>{o.cancelText || "Cancel"}</AlertDialogCancel>
            <AlertDialogAction
              data-testid="confirm-ok"
              onClick={() => close(true)}
              className={destructive ? "bg-red-600 hover:bg-red-700 text-white" : "bg-cass-navy hover:bg-cass-navy/90 text-white"}>
              {o.confirmText || (destructive ? "Delete" : "Confirm")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    );
  }

  return (
    <Dialog open onOpenChange={(open) => { if (!open) close(null); }}>
      <DialogContent className="max-w-md" data-testid="prompt-dialog">
        <DialogHeader>
          <DialogTitle>{o.title || "Enter a value"}</DialogTitle>
          {o.description ? <DialogDescription>{o.description}</DialogDescription> : <DialogDescription className="sr-only">{o.title || "Enter a value"}</DialogDescription>}
        </DialogHeader>
        <div className="space-y-2">
          {o.label && <Label>{o.label}</Label>}
          <Input
            autoFocus
            value={val}
            placeholder={o.placeholder || ""}
            onChange={(e) => setVal(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") close(val); }}
            data-testid="prompt-input"
          />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => close(null)} data-testid="prompt-cancel">Cancel</Button>
          <Button className="bg-cass-navy text-white" onClick={() => close(val)} data-testid="prompt-ok">{o.confirmText || "Save"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
